import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { TICKET_PRIORITY_CONFIG, TICKET_CATEGORY_CONFIG, TicketPriority, TicketCategory, TicketAttachment } from './types';
import { Plus, X, Upload, FileText } from 'lucide-react';
import { uploadToR2 } from '@/lib/r2-storage';
import { toast } from 'sonner';
import type { WorkspaceMemberWithProfile } from '@/lib/workspaceMembers';

interface CreateTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    subject: string;
    description: string;
    priority: TicketPriority;
    category: TicketCategory;
    clientId?: string | null;
    assignedTo?: string | null;
    attachments: TicketAttachment[];
  }) => Promise<any>;
  clients: Array<{ id: string; name: string; email: string; avatar?: string | null }>;
  members: WorkspaceMemberWithProfile[];
  submitting?: boolean;
  isClientPortal?: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const CreateTicketDialog: React.FC<CreateTicketDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  clients,
  members,
  submitting = false,
  isClientPortal = false,
}) => {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [category, setCategory] = useState<TicketCategory>('general');
  const [clientId, setClientId] = useState<string>('none');
  const [assignedTo, setAssignedTo] = useState<string>('none');
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setSubject('');
    setDescription('');
    setPriority('normal');
    setCategory('general');
    setClientId('none');
    setAssignedTo('none');
    setAttachments([]);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large (max 5MB)`);
        continue;
      }
      if (!file.type.match(/^image\/(jpeg|png)$/)) {
        toast.error(`${file.name} is not a valid image format (JPG/PNG only)`);
        continue;
      }
      setUploading(true);
      try {
        const fileName = `ticket-${Date.now()}-${file.name}`;
        const { publicUrl } = await uploadToR2({ fileName, file, folder: 'ticket-attachments' });
        setAttachments(prev => [
          ...prev,
          { name: file.name, url: publicUrl, size: file.size, type: file.type },
        ]);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      } finally {
        setUploading(false);
      }
    }
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) return;
    await onSubmit({
      subject: subject.trim(),
      description: description.trim(),
      priority,
      category,
      clientId: clientId === 'none' ? null : clientId,
      assignedTo: assignedTo === 'none' ? null : assignedTo,
      attachments,
    });
    reset();
    onOpenChange(false);
  };

  const canSubmit = subject.trim().length > 0 && description.trim().length > 0 && !submitting && !uploading;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl bg-card border-border p-0 overflow-hidden">
        <DialogDescription className="sr-only">Create a new support ticket</DialogDescription>
        <DialogHeader className="px-6 py-4 border-b border-border bg-muted/20">
          <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
            <Plus className="w-5 h-5 text-workspace-accent" />
            New Support Ticket
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[80vh]">
          <div className="p-6 space-y-5">
            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-foreground flex items-center gap-1">
                Subject <span className="text-red-500">*</span>
              </Label>
              <Input
                placeholder="Brief description of the issue..."
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="bg-background border-input text-foreground h-11"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-foreground flex items-center gap-1">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="Provide a detailed description of the issue, steps to reproduce, or any relevant context..."
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="bg-background border-input text-foreground min-h-[120px] resize-none"
              />
            </div>

            {/* Priority + Category */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Priority</Label>
                <Select value={priority} onValueChange={v => setPriority(v as TicketPriority)}>
                  <SelectTrigger className="bg-background border-input text-foreground h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {Object.entries(TICKET_PRIORITY_CONFIG).map(([val, cfg]) => (
                      <SelectItem key={val} value={val}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Category</Label>
                <Select value={category} onValueChange={v => setCategory(v as TicketCategory)}>
                  <SelectTrigger className="bg-background border-input text-foreground h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {Object.entries(TICKET_CATEGORY_CONFIG).map(([val, cfg]) => (
                      <SelectItem key={val} value={val}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Client + Assignee */}
            {!isClientPortal && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Client (Optional)</Label>
                  <Select value={clientId} onValueChange={setClientId}>
                    <SelectTrigger className="bg-background border-input text-foreground h-11">
                      <SelectValue placeholder="Select client..." />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="none">No client</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Assign To (Optional)</Label>
                  <Select value={assignedTo} onValueChange={setAssignedTo}>
                    <SelectTrigger className="bg-background border-input text-foreground h-11">
                      <SelectValue placeholder="Select member..." />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="none">Unassigned</SelectItem>
                      {members.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Attachments */}
            <div className="space-y-2">
              <Label className="text-foreground">Attachments</Label>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-border rounded-lg p-4 flex items-center gap-3 cursor-pointer hover:border-workspace-accent/50 hover:bg-muted/30 transition-colors"
              >
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {uploading ? 'Uploading...' : 'Click to attach files (JPG/PNG, max 5MB each)'}
                </span>
                <input type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" ref={fileRef} multiple onChange={handleFileUpload} className="hidden" />
              </div>
              {attachments.length > 0 && (
                <div className="space-y-2 mt-2">
                  {attachments.map((att, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                      <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm flex-1 truncate text-foreground">{att.name}</span>
                      <span className="text-xs text-muted-foreground">{(att.size / 1024).toFixed(0)}KB</span>
                      <button
                        onClick={e => { e.stopPropagation(); setAttachments(prev => prev.filter((_, idx) => idx !== i)); }}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-red-500 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="px-6 py-4 border-t border-border bg-muted/20 gap-3">
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} className="h-11 px-6">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-workspace-accent text-white hover:bg-workspace-accent-hover h-11 px-8 shadow-md hover:shadow-lg transition-all"
          >
            {submitting ? 'Creating...' : 'Create Ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
